import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Wordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, Save, Share2, Printer, RotateCcw, ListRestart, CheckCircle2, Upload, Video } from "lucide-react";
import { timeLabel, isEspresso } from "@/lib/brew";
import { shareRecipe, printRecipe } from "@/lib/share";
import { usePrefs } from "@/context/PrefsContext";
import { fmtWeight, fmtVolume, fmtTemp } from "@/lib/units";
import { BrewProgress } from "@/components/BrewProgress";

export default function Results() {
  const location = useLocation();
  const navigate = useNavigate();
  const recipe = location.state?.recipe;
  const historyId = location.state?.historyId;
  const total = location.state?.total ?? recipe?.duration ?? 0;
  const [saved, setSaved] = useState(false);
  const [brewNotes, setBrewNotes] = useState(""), [tastingNotes, setTastingNotes] = useState("");
  const [media, setMedia] = useState([]), [journalSaved, setJournalSaved] = useState(false), [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const { prefs } = usePrefs();

  if (!recipe) {
    navigate("/recipes", { replace: true });
    return null;
  }

  const esp = isEspresso(recipe.brewer);
  const completed = new Date().toLocaleString();

  const save = async () => {
    try {
      await api.post("/recipes", recipe);
      setSaved(true);
      toast.success("Recipe saved");
    } catch {
      toast.error("Could not save recipe");
    }
  };

  const brewAgain = () => {
    localStorage.setItem("bm_active_brew", JSON.stringify({ id: crypto.randomUUID(), recipe, steps: recipe.instructions, total, status: "preparing", elapsedBase: 0, startedAt: null }));
    navigate("/brew", { replace: true });
  };

  const saveJournal = async () => {
    if (!historyId) return toast.error("This brew could not be linked to History");
    try {
      const { data } = await api.put(`/brew-history/${historyId}`, { brew_notes: brewNotes, tasting_notes: tastingNotes });
      setMedia(data.media || media); setJournalSaved(true); toast.success("Brew journal saved");
    } catch { toast.error("Could not save your brew journal"); }
  };

  const uploadMedia = async (event) => {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file || !historyId) return;
    if (file.size > 50 * 1024 * 1024) return toast.error("Choose a photo or video smaller than 50 MB");
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post(`/brew-history/${historyId}/media`, form);
      setMedia(data.media || []); toast.success("Brew photo or video added");
    } catch { toast.error("Could not upload this photo or video"); }
    finally { setUploading(false); }
  };

  return (
    <div className="px-5 pt-10 pb-10" data-testid="results-page">
      <BrewProgress current="Ready" />
      <div className="flex flex-col items-center text-center">
        <CheckCircle2 size={56} className="text-[#2E5E3E]" />
        <h1 className="mt-4 text-3xl font-semibold text-[#1F2A44]">Your brew is ready.</h1>
        <p className="mt-1 text-sm text-[#1F2A44]/70">Enjoy your <Wordmark className="text-[#1F2A44]" /> cup.</p>
      </div>

      <div className="mt-8 rounded-3xl border border-[#D8C3A5]/25 bg-[#2E5E3E] p-5" data-testid="results-summary">
        <div className="text-lg font-semibold text-[#EDE0CC]">{recipe.name}</div>
        <div className="mt-3 grid grid-cols-2 gap-y-2 text-sm text-[#EDE0CC]/90">
          <span>Brewer</span><span className="text-right font-semibold">{recipe.brewer}</span>
          <span>Mode</span><span className="text-right font-semibold">{recipe.mode === "guided" ? "Guided Recipe" : "Custom Brew"}</span>
          <span>Roast</span><span className="text-right font-semibold">{recipe.roast}</span>
          <span>Coffee</span><span className="text-right font-semibold">{fmtWeight(recipe.coffee, prefs)}</span>
          {esp ? (<><span>Yield</span><span className="text-right font-semibold">{fmtWeight(recipe.yield_g, prefs)}</span></>)
               : (<><span>Water</span><span className="text-right font-semibold">{fmtVolume(recipe.water, prefs)}</span></>)}
          <span>Ratio</span><span className="text-right font-semibold">1:{recipe.ratio}</span>
          <span>Grind</span><span className="text-right font-semibold">{recipe.grind}</span>
          <span>Temperature</span><span className="text-right font-semibold">{fmtTemp(recipe.temperature, prefs)}</span>
          <span>Total time</span><span className="text-right font-semibold">{timeLabel(total)}</span>
          <span>Completed</span><span className="text-right font-semibold">{completed}</span>
        </div>
        {recipe.notes ? <p className="mt-3 text-sm text-[#EDE0CC]/70">Notes: {recipe.notes}</p> : null}
      </div>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <ActionBtn onClick={save} icon={Save} label={saved ? "Saved" : "Save"} testid="results-save-btn" />
        <ActionBtn onClick={() => shareRecipe(recipe)} icon={Share2} label="Share" testid="results-share-btn" />
        <ActionBtn onClick={() => printRecipe(recipe)} icon={Printer} label="Print" testid="results-print-btn" />
      </div>

      <section className="mt-6 rounded-3xl border border-[#D8C3A5]/25 bg-[#2E5E3E] p-5" data-testid="brew-journal">
        <h2 className="text-lg font-semibold text-[#EDE0CC]">Your brew journal</h2>
        <p className="mt-1 text-xs leading-5 text-[#EDE0CC]/60">Record what happened during this brew and the flavours you found. These notes are private to your account.</p>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[#EDE0CC]/60">Brew notes</label>
        <textarea value={brewNotes} onChange={(e) => { setBrewNotes(e.target.value); setJournalSaved(false); }} rows={3} className="mt-2 w-full rounded-xl border border-[#EDE0CC]/25 bg-[#234A31] p-3 text-sm text-[#EDE0CC] placeholder:text-[#EDE0CC]/45" placeholder="Dose changes, pouring technique, drawdown, problems or improvements for next time…" />
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[#EDE0CC]/60">Tasting notes</label>
        <textarea value={tastingNotes} onChange={(e) => { setTastingNotes(e.target.value); setJournalSaved(false); }} rows={3} className="mt-2 w-full rounded-xl border border-[#EDE0CC]/25 bg-[#234A31] p-3 text-sm text-[#EDE0CC] placeholder:text-[#EDE0CC]/45" placeholder="Aroma, sweetness, acidity, body, finish and flavours you noticed…" />
        <input ref={fileRef} type="file" accept="image/*,video/*" capture="environment" onChange={uploadMedia} className="hidden" />
        <div className="mt-4 grid grid-cols-2 gap-2"><button onClick={saveJournal} className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#F57C00] text-sm font-semibold text-[#1F2A44]"><Save size={16}/>{journalSaved ? "Journal saved" : "Save journal"}</button><button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#F57C00] text-sm font-semibold text-[#1F2A44] disabled:opacity-50"><Camera size={16}/>{uploading ? "Uploading…" : "Photo or video"}</button></div>
        {media.length > 0 && <div className="mt-4 grid grid-cols-3 gap-2">{media.map((item, index) => item.type?.startsWith("video/") ? <a key={item.path || index} href={item.url} target="_blank" rel="noreferrer" className="flex aspect-square items-center justify-center rounded-xl bg-[#234A31] text-[#EDE0CC]/70"><Video size={25}/></a> : <a key={item.path || index} href={item.url} target="_blank" rel="noreferrer"><img src={item.url} alt="Customer brew" className="aspect-square w-full rounded-xl object-cover" /></a>)}</div>}
        <div className="mt-3 flex items-center gap-2 text-xs text-[#EDE0CC]/45"><Upload size={13}/> Maximum file size: 50 MB</div>
      </section>

      <Button onClick={brewAgain} data-testid="brew-again-btn" className="mt-5 w-full h-13 rounded-full bg-[#F57C00] text-[#1F2A44] font-semibold hover:bg-[#E86F00] active:scale-95 transition-all flex items-center justify-center gap-2 py-3">
        <RotateCcw size={18} /> Brew Again
      </Button>
      <Button onClick={() => navigate("/brew-guide")} data-testid="return-brew-btn" className="mt-3 w-full h-13 rounded-full bg-[#F57C00] text-[#1F2A44] hover:bg-[#E86F00] flex items-center justify-center gap-2 py-3">
        <ListRestart size={18} /> Return to Brew
      </Button>
    </div>
  );
}

function ActionBtn({ onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="flex flex-col items-center gap-1 h-16 rounded-2xl border border-[#F57C00] bg-[#F57C00] text-[#1F2A44] active:scale-95 transition-transform hover:bg-[#E86F00]">
      <Icon size={18} />
      <span className="text-xs font-semibold">{label}</span>
    </button>
  );
}
